import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchTransportationModes } from '../data-provider';
import type { TransportationMode, TransportationModeFilters, TransportationModes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal transportation mode lookup cache used to resolve transportation mode references
 * without repeatedly requesting the same transportation mode data.
 */

export const transportationModesQueryOptions = (
  filters: Partial<TransportationModeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const transportationModes = await fetchTransportationModes(filters, { signal });
      put(transportationModes.list);
      return transportationModes;
    },
    queryKey: CACHE_KEYS.transportationModes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_TRANSPORTATION_MODES_CACHE_KEY = btoa('internal-transportation-modes-cache-key');

const INTERNAL_QUERY_TRANSPORTATION_MODES_KEY = transportationModesQueryOptions({
  codeDesc: INTERNAL_TRANSPORTATION_MODES_CACHE_KEY,
}).queryKey;

const getTransportationModeFallback = (transportationModeId: string): TransportationMode => {
  logger.warn(`Transportation mode "${transportationModeId}" was not found. Verify the data model.`);
  return {
    codeDesc: transportationModeId,
    id: transportationModeId,
    label: transportationModeId,
    lookupCodeType: 'QUTEMODE',
  };
};

export const put = async (transportationModes: TransportationMode[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TRANSPORTATION_MODES_KEY);

  const newTransportationModes = transportationModes.reduce(
    (acc, transportationMode) => {
      acc[String(transportationMode.id)] = transportationMode;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_TRANSPORTATION_MODES_KEY, {
    list: Object.values(newTransportationModes),
    map: newTransportationModes,
  });
};

export const getAll = (): TransportationModes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_TRANSPORTATION_MODES_KEY);
};

export const find = async (transportationModeId: string): Promise<TransportationMode> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TRANSPORTATION_MODES_KEY);

  const transportationMode = data?.map[transportationModeId];
  if (transportationMode) {
    return transportationMode;
  }

  try {
    const newData = await queryClient.query(
      transportationModesQueryOptions({ search: { codeId: transportationModeId } }),
    );
    if (newData.map[transportationModeId]) {
      void put(newData.list);
      return newData.map[transportationModeId];
    }
  } catch {
    return getTransportationModeFallback(transportationModeId);
  }

  return getTransportationModeFallback(transportationModeId);
};

export const findAll = async (transportationModeIds: string[]): Promise<TransportationMode[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_TRANSPORTATION_MODES_KEY);

  const missingIds: string[] = [];
  const transportationModes: TransportationMode[] = [];

  for (const transportationModeId of transportationModeIds) {
    const item = data?.map[transportationModeId];
    if (item) {
      transportationModes.push(item);
    } else {
      missingIds.push(transportationModeId);
    }
  }

  if (!missingIds.length) {
    return transportationModes;
  }

  try {
    const newData = await queryClient.query(
      transportationModesQueryOptions({ search: { codeId: missingIds.join(',') } }),
    );
    void put(newData.list);
    for (const missingId of missingIds) {
      const item = newData.map[missingId];
      if (item) {
        transportationModes.push(item);
      } else {
        transportationModes.push(getTransportationModeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      transportationModes.push(getTransportationModeFallback(missingId));
    }
  }

  return transportationModes;
};
